import moment from 'moment';

/** Display name without .pdf extension (dashboard / editor top bar). */
export function displayFileName(documentName?: string | null): string {
  if (!documentName) return '';
  return documentName.replace(/\.pdf$/i, '');
}

/** Normalize user input to PdfDocument.name (always ends with .pdf). */
export function toDocumentFileName(input: string): string {
  const trimmed = (input || '').trim().replace(/\.pdf$/i, '');
  const sanitized = trimmed
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `${sanitized || 'factsheet'}.pdf`;
}

/** Slug part for download filenames (no .pdf). */
export function slugifyFileNamePart(input?: string | null): string {
  const trimmed = String(input || '')
    .trim()
    .replace(/\.pdf$/i, '');
  const sanitized = trimmed
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return sanitized || 'Factsheet';
}

/** Parse as-of / published dates into a moment (falls back to today). */
function parseAsOfMoment(raw?: string | Date | null): moment.Moment {
  if (raw == null || raw === '') return moment();
  const asString = typeof raw === 'string' ? raw.trim() : raw;
  const isoPart =
    typeof asString === 'string' ? asString.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] : undefined;
  if (isoPart && moment(isoPart, 'YYYY-MM-DD', true).isValid()) {
    return moment(isoPart, 'YYYY-MM-DD');
  }
  const de = moment(asString, ['DD.MM.YYYY', 'D.M.YYYY', 'YYYY-MM-DD'], true);
  if (de.isValid()) return de;
  const m = moment(asString);
  return m.isValid() ? m : moment();
}

/** Parse as-of / published dates into YYYYMMDD. */
export function formatAsOfYyyymmdd(raw?: string | Date | null): string {
  return parseAsOfMoment(raw).format('YYYYMMDD');
}

/** Parse as-of / published dates into DD.MM.YYYY (German display). */
export function formatAsOfDdMmYyyy(raw?: string | Date | null): string {
  return parseAsOfMoment(raw).format('DD.MM.YYYY');
}

/**
 * Expand `{date}` placeholders in a custom download-name template.
 * `{date}` → YYYYMMDD from the factsheet's as-of date (falls back to today).
 *
 * Example:  "Factsheet_FS_SQUAD_Value_B_{date}" → "Factsheet_FS_SQUAD_Value_B_20260531"
 */
export function applyDownloadNamePlaceholders(
  template: string,
  asOf?: string | Date | null,
): string {
  const ymd = formatAsOfYyyymmdd(asOf);
  return template.replace(/\{date\}/gi, ymd);
}

/**
 * Expand `{date}` placeholders in display text (section headers, captions).
 * `{date}` → DD.MM.YYYY from the factsheet's as-of date (falls back to today).
 *
 * Example:  "Wertentwicklung und Risikokennzahlen per {date}" → "… per 31.05.2026"
 */
export function applyDisplayDatePlaceholders(
  template: string,
  asOf?: string | Date | null,
): string {
  if (!template || !/\{date\}/i.test(template)) return template;
  const display = formatAsOfDdMmYyyy(asOf);
  return template.replace(/\{date\}/gi, display);
}

/**
 * Build the browser download filename.
 *
 * - If `customTemplate` is provided (non-empty after trimming) it is used as-is
 *   after expanding `{date}` placeholders and stripping an existing .pdf suffix.
 * - Otherwise falls back to the auto-generated `Factsheet_{FundTitle}_{YYYYMMDD}` pattern.
 *
 * The returned string always ends with `.pdf`.
 *
 * @example
 *   buildFactsheetDownloadFileName({ customTemplate: 'FS_SQUAD_Value_B_{date}', asOf: '2026-05-31' })
 *   // → 'FS_SQUAD_Value_B_20260531.pdf'
 *
 *   buildFactsheetDownloadFileName({ title: 'SQUAD Value A', asOf: '2026-05-31' })
 *   // → 'Factsheet_SQUAD-Value-A_20260531.pdf'
 */
export function buildFactsheetDownloadFileName(opts: {
  title?: string | null;
  asOf?: string | Date | null;
  /** Optional user-defined name template, may contain `{date}`. */
  customTemplate?: string | null;
}): string {
  const custom = (opts.customTemplate || '').trim().replace(/\.pdf$/i, '');
  if (custom) {
    const expanded = applyDownloadNamePlaceholders(custom, opts.asOf);
    return toDocumentFileName(expanded);
  }
  const slug = slugifyFileNamePart(opts.title);
  const ymd = formatAsOfYyyymmdd(opts.asOf);
  return `Factsheet_${slug}_${ymd}.pdf`;
}

/** Resolve as-of for factsheet download naming from common V2 field shapes. */
export function resolveFactsheetDownloadAsOf(pdf?: {
  data_as_of?: string;
  historyUntil?: string;
  published_at?: string | Date;
  preise?: { kursper?: string };
} | null): string | Date | undefined {
  return (
    pdf?.preise?.kursper ||
    pdf?.data_as_of ||
    pdf?.historyUntil ||
    pdf?.published_at ||
    undefined
  );
}

/** Trigger a browser download for a Blob with a fixed filename. */
export function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = url;
  a.download = toDocumentFileName(fileName);
  window.document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  window.document.body.removeChild(a);
}
